import { useMutation, useQueryClient } from "@tanstack/react-query"
import { memo, useState } from "react"
import { Controller, type SubmitHandler, useForm } from "react-hook-form"
import { FaPlus } from "react-icons/fa"

import type { ApiError, UserCreate } from "@/client"
import { Checkbox } from "@/components/atoms/Checkbox"
import { FormDialog } from "@/components/molecules/FormDialog"
import { FormField } from "@/components/molecules/FormField"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"
import { emailPattern, handleError } from "@/utils"
import { Button, Flex, Input, VStack } from "@chakra-ui/react"
import { Field } from "@chakra-ui/react"

interface UserCreateForm extends UserCreate {
  confirm_password: string
}

const AddUser = memo(() => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const form = useForm<UserCreateForm>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      email: "",
      full_name: "",
      password: "",
      confirm_password: "",
      is_active: false,
    },
  })

  const mutation = useMutation({
    mutationFn: (data: UserCreate) =>
      api.admin.createUserApiV1AdminUsersPost({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast("User created successfully.")
      form.reset()
      setIsOpen(false)
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const onSubmit: SubmitHandler<UserCreateForm> = (data) => {
    mutation.mutate(data)
  }

  return (
    <>
      <Button value="add-user" my={4} onClick={() => setIsOpen(true)}>
        <FaPlus fontSize="16px" />
        Add User
      </Button>
      <FormDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Add User"
        onSubmit={form.handleSubmit(onSubmit)}
        submitText="Save"
        isLoading={mutation.isPending}
      >
        <VStack gap={4}>
          <FormField
            required
            isInvalid={!!form.formState.errors.email}
            errorText={form.formState.errors.email?.message}
            label="Email"
          >
            <Input
              id="email"
              {...form.register("email", {
                required: "Email is required",
                pattern: emailPattern,
              })}
              placeholder="Email"
              type="email"
            />
          </FormField>

          <FormField
            isInvalid={!!form.formState.errors.full_name}
            errorText={form.formState.errors.full_name?.message}
            label="Full Name"
          >
            <Input
              id="name"
              {...form.register("full_name")}
              placeholder="Full name"
              type="text"
            />
          </FormField>

          <FormField
            required
            isInvalid={!!form.formState.errors.password}
            errorText={form.formState.errors.password?.message}
            label="Set Password"
          >
            <Input
              id="password"
              {...form.register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              placeholder="Password"
              type="password"
            />
          </FormField>

          <FormField
            required
            isInvalid={!!form.formState.errors.confirm_password}
            errorText={form.formState.errors.confirm_password?.message}
            label="Confirm Password"
          >
            <Input
              id="confirm_password"
              {...form.register("confirm_password", {
                required: "Please confirm your password",
                validate: (value) =>
                  value === form.getValues().password ||
                  "The passwords do not match",
              })}
              placeholder="Password"
              type="password"
            />
          </FormField>

          <Flex mt={4} direction="column" gap={4}>
            <Controller
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <Field.Root disabled={field.disabled}>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={({ checked }: { checked: boolean }) =>
                      field.onChange(checked)
                    }
                  >
                    Is active?
                  </Checkbox>
                </Field.Root>
              )}
            />
          </Flex>
        </VStack>
      </FormDialog>
    </>
  )
})

AddUser.displayName = "AddUser"

export default AddUser
